/**
 * The shape of the sign-in context, and its empty default.
 *
 * Kept apart from AuthContext.tsx (the provider), as the other contexts'
 * definitions are, so that file exports only its component. Read it through
 * shared/hooks/useAuth.
 *
 * @module AuthContextDefinition
 */

import { User, UserCredential } from 'firebase/auth';
import { createContext } from 'react';
import type { MySQLUser } from '../types/user';
import type { AccountRead } from './accountRead';

/** Who is signed in, and the ways to sign in and out. */
export interface AuthContextType {
  /** The Firebase identity, or null when nobody is signed in. */
  currentUser: User | null;
  /** The Grow account behind it (api/users), or null until read or when there is none yet (CompleteSignup). */
  mysqlUser: MySQLUser | null;
  /**
   * True when the account could not be read at all (a 500, no connection):
   * Layout shows the standard failure page, never the sign-up path, which
   * would try to make a second account (D31).
   */
  accountFailed: boolean;
  /**
   * True when that failure was the server's request limit (429 RATE_LIMITED)
   * on every try: the failure page then says the limit's calm sentence
   * (serverFailure.limited), not "something went wrong on our side" (B24).
   */
  accountLimited: boolean;
  /** The short reference code of the account failure's Sentry report (E3), shown on the failure page; undefined when Sentry is off or nothing failed. */
  accountReference?: string;
  /** True until Firebase has said who is signed in and the account has been asked for; nothing under AuthProvider is drawn meanwhile. */
  loading: boolean;
  /** Google sign-in: a popup in a browser tab, a redirect in the installed app (then it resolves null). */
  signInWithGoogle: () => Promise<UserCredential | null>;
  /** Facebook sign-in, as Google's. */
  signInWithFacebook: () => Promise<UserCredential | null>;
  /** With the ID token Google One Tap hands back. */
  signInWithGoogleIdToken: (idToken: string) => Promise<UserCredential | null>;
  /** Why a sign-in that went to Google or Facebook and back did not finish. */
  redirectError: string | null;
  /** Email and password sign-in. */
  signInWithEmail: (email: string, password: string) => Promise<UserCredential | null>;
  /** Makes the Firebase identity; the Grow account is made after (CompleteSignup). */
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<UserCredential | null>;
  /** Signs out, clearing the toasts and the query cache and forgetting the garden being tended on this device. */
  logout: () => Promise<void>;
  /** Merges a change already saved on the server into the account held here. */
  updateMysqlUser: (user: Partial<MySQLUser>) => void;
  /** Re-read the Grow account after signup creates one; 'ok' when an account came back, 'none' when there is none, 'failed' when the read failed (AccountRead). */
  refreshMysqlUser: () => Promise<AccountRead>;
  /** Firebase's own reset mail, when the server could not send ours. */
  sendResetEmailDirect: (email: string) => Promise<void>;
}

/** The context, empty until AuthProvider fills it. */
export const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  mysqlUser: null,
  accountFailed: false,
  accountLimited: false,
  loading: true,
  signInWithGoogle: async () => null,
  signInWithFacebook: async () => null,
  signInWithGoogleIdToken: async () => null,
  redirectError: null,
  signInWithEmail: async () => null,
  signUpWithEmail: async () => null,
  logout: async () => {},
  updateMysqlUser: () => {},
  refreshMysqlUser: async () => 'failed',
  sendResetEmailDirect: async () => {},
});